import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { findSubroutine } from '@/util/application';
import { arityScope, boundSubroutine } from '@/util/arity';
import { isArray, isNumber, isObject, isVariable } from '@/util/typeGuards';
import { getChipType } from '@/util/variables';
import { validateListArity } from './arity';
import { valueUtil } from './value';

export const returnvaluesUtil = (): ValidationObject<Detail[] | undefined> => {
  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!isNumber(linkedSubroutineId)) return errors;

    const linkedSubroutine = findSubroutine(application.subroutines, linkedSubroutineId);

    if (!linkedSubroutine) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is referring to a subroutine that doesn't exist`,
        id: nodeId
      });
      return errors;
    }

    const scope = arityScope(application, metadata);
    errors.push(...validateListArity(configuration, details, nodeId, blockName, scope));

    // The subroutine whose return types the values are measured against, for appreturnvalues that is the app entry.
    const bound = boundSubroutine(configuration, details, nodeId, scope);

    getValue(configuration, details)?.forEach((value, index) => {
      const valueErrors = valueUtil().validate(
        application,
        metadata,
        linkedSubroutineId,
        nodeId,
        blockName,
        configuration,
        value
      );
      if (valueErrors.length) {
        errors.push(...valueErrors);
        return;
      }

      // A row the subroutine declares no return type for is reported by its arity, not measured against a type.
      const returnType = bound?.return_types[index]?.type;
      if (!isVariable(value) || !returnType) return;

      const variableType = getChipType(value, application, metadata, linkedSubroutineId);
      if (variableType && returnType !== variableType) {
        errors.push({
          severity: 'error',
          message: `Block ${blockName} requires type: ${returnType} but variable is of type: ${variableType}`,
          id: nodeId
        });
      }
    });

    return errors;
  };

  const getValue = (configuration: Configuration, obj: unknown): Detail[] | undefined => {
    const initVal = obj;
    if (isArray(initVal)) {
      return initVal as Detail[];
    } else if (isObject(initVal)) {
      const prop = initVal[configuration.value];
      return getValue(configuration, prop);
    }
    return;
  };

  return {
    validate: validate,
    getValue: getValue
  };
};
